import {
  BehaviorSubject,
  Observable,
  of,
  throwError as observableThrowError,
  interval,
} from 'rxjs';
import { catchError, map, share, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { ResetExploreState } from 'app/explore/state/explore.actions';
import { ResetGroupManagementState } from 'app/group-management/state/group-management.actions';
import { environment } from '../../environments/environment';
import { User } from '../classes/user';
import { AlertDialogComponent } from '../components/alert-dialog/alert-dialog.component';
import { EditProfileDialogComponent } from '../components/edit-profile-dialog/edit-profile-dialog.component';
import { RedirectDialogComponent } from '../components/redirect-dialog/redirect-dialog.component';
import { OfflineService } from './offline.service';
import { jwtDecode } from 'jwt-decode';
import { ExtendSessionComponent } from 'app/components/extend-session/extend-session.component';

@Injectable()
export class AuthService {
  static readonly authTokenKey = 'midas-token';
  static readonly usernameKey = 'midas-username';
  static readonly userUUIDKey = 'midas-userUUID';
  static readonly userKey = 'midas-user';

  isLoggedIn = false;
  currentUserObs: Observable<User>;
  approvedEmailDomains: string[] = [];

  private currentUser: User;
  private currentUserSubject: BehaviorSubject<User>;
  private extendSessionDialogOpen = false;

  /**
   * Upon initialization, checks if the user has an auth token in localStorage,
   * and sets the currentUser property from localStorage
   *
   * If the user is not logged in, they are routed to /login.
   */
  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private offline: OfflineService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private store: Store,
  ) {
    this.currentUserSubject = new BehaviorSubject(null);
    if (
      localStorage.getItem(AuthService.authTokenKey) &&
      localStorage.getItem(AuthService.userKey)
    ) {
      this.isLoggedIn = true;
      this.currentUser = new User(
        JSON.parse(localStorage.getItem(AuthService.userKey)),
      );
      this.currentUserSubject.next(this.currentUser);
    } else {
      this.isLoggedIn = false;
      this.currentUserSubject.next(null);
      this.router.navigate(['login']);
    }
    this.currentUserObs = this.currentUserSubject.asObservable();
  }

  /**
   * Login to the server given credentials. Save the username and token to localStorage.
   *
   * @param  {string} emailAddress - credentials to login with
   * @param  {string} password - credentials to login with
   * @returns {Observable} - hot observable that executes the login request
   */
  login(emailAddress: string, password: string): Observable<any> {
    const loginObs = this.httpClient
      .post(
        environment.applySubstitutions(environment.api_url) + 'api/Auth/login',
        {
          email: emailAddress,
          password: password,
        },
      )
      .pipe(
        catchError(err => observableThrowError(err)),
        share(),
      );

    loginObs.subscribe(results => {
      if (results['status'] === 'success') {
        localStorage.setItem(AuthService.usernameKey, emailAddress);
        localStorage.setItem(AuthService.authTokenKey, results['auth_token']);
        localStorage.setItem(AuthService.userUUIDKey, results['user_uuid']);
      } else {
        throw new Error(results['message']);
      }
    });

    return loginObs;
  }

  /**
   * Get the profile picture for the user from server.
   *
   * @param  {} userUUID - id of the user
   */
  getUserImage(userUUID) {
    return this.httpClient
      .get(
        environment.applySubstitutions(environment.api_url) +
          'api/User/' +
          userUUID +
          '/image',
        {
          responseType: 'blob',
        },
      )
      .pipe(
        map(data => {
          this.currentUser.profilePicture =
            this.sanitizer.bypassSecurityTrustUrl(
              window.URL.createObjectURL(data),
            );
          localStorage.setItem(
            AuthService.userKey,
            JSON.stringify(this.currentUser),
          );
          this.currentUserSubject.next(this.currentUser);
        }),
      );
  }

  /**
   * Logout of the server. Remove the saved items from localStorage.
   */
  logout() {
    const logoutObs = this.httpClient
      .post(
        environment.applySubstitutions(environment.api_url) + 'api/Auth/logout',
        null,
        {
          responseType: 'text',
        },
      )
      .pipe(share());

    logoutObs.subscribe(() => {
      this.isLoggedIn = false;
      this.currentUser = null;
      this.currentUserSubject.next(null);
      localStorage.removeItem(AuthService.userKey);
      localStorage.removeItem(AuthService.usernameKey);
      localStorage.removeItem(AuthService.authTokenKey);
      localStorage.removeItem(AuthService.userUUIDKey);
      this.store.dispatch(new ResetExploreState());
      this.store.dispatch(new ResetGroupManagementState());
      this.router.navigate(['login']);
    });

    return logoutObs;
  }

  /**
   * If the app is online, the current user session is validated with the server and
   * saves the results to localStorage
   *
   * If the app is offline, the service is initialized from cached values in localStorage as
   * long as the auth token exists.
   */
  checkForLogin(): Observable<User> {
    if (
      !this.offline.isOnline() &&
      localStorage.getItem(AuthService.authTokenKey) &&
      localStorage.getItem(AuthService.usernameKey) &&
      localStorage.getItem(AuthService.userKey)
    ) {
      this.isLoggedIn = true;
      this.currentUser = new User(
        JSON.parse(localStorage.getItem(AuthService.userKey)),
      );
      this.currentUserSubject.next(this.currentUser);

      return of(this.currentUser);
    } else if (this.offline.isOnline() || this.offline.isNavigatorOnline()) {
      return this.httpClient
        .get(
          environment.applySubstitutions(environment.api_url) +
            'api/Auth/status',
        )
        .pipe(
          map((results: any) => {
            const { user, auth_token } = results;
            // TODO: Update with better verification status of whether user is only a Basic User
            const isNotBasicUser = user?.roles.some(role =>
              role.permissions.some(
                permission =>
                  permission.uuid === environment.plannerAccessPermissionUuid,
              ),
            );
            if (!isNotBasicUser) {
              this.redirectToMobile();
            } else {
              this.isLoggedIn = true;
              this.currentUser = new User({
                uuid: user.uuid,
                confirmed: user.confirmed,
                email_domain_approved: user.email_domain_approved,
                email: user.email,
                firstName: user.firstname,
                hasProfilePic: !!user.image_name,
                lastName: user.lastname,
                rank: user.rank,
                isAdmin:
                  user.admin ||
                  (user.roles && user.roles.some(role => role.is_admin)),
                roles: user.roles,
              });
              localStorage.setItem(
                AuthService.userKey,
                JSON.stringify(this.currentUser),
              );
              localStorage.setItem(AuthService.authTokenKey, auth_token);
              this.currentUserSubject.next(this.currentUser);

              if (this.currentUser.hasProfilePic) {
                this.getUserImage(this.currentUser.uuid).subscribe();
              }
            }

            return this.currentUser;
          }),
          catchError(e => {
            // log user out
            this.isLoggedIn = false;
            this.currentUserSubject.next(null);
            this.router.navigate(['login']);
            return observableThrowError(e.message || 'Server error');
          }),
          share(),
        );
    }

    this.isLoggedIn = false;
    return of(null);
  }

  redirectToMobile(): void {
    const errorMessage =
      'You do not have sufficient permissions to access FitForce Planner. Please contact us (' +
      environment.externalSupportUrl +
      ') if your role requires the use of Planner. ' +
      'Please log into FitForce Mobile at ' +
      environment.mobileUrl +
      ' to begin your workout.';
    this.dialog.open(RedirectDialogComponent, {
      width: '500px',
      data: {},
    });
    throw new Error(errorMessage);
  }

  /**
   * Change the password of the account.
   *
   * @param  {string} old - old password
   * @param  {string} newPW - new password
   */
  changePassword(old: string, newPW: string) {
    return this.httpClient
      .post(
        environment.applySubstitutions(environment.api_url) +
          'api/Auth/password',
        {
          oldPassword: old,
          password: newPW,
        },
      )
      .pipe(
        map(res => {
          localStorage.setItem(AuthService.authTokenKey, res['auth_token']);
          return res;
        }),
        catchError(e => {
          return observableThrowError(e.message || 'Server error');
        }),
      );
  }

  /**
   * If the profile picture hasn't been loaded from the server yet, subscribe to the observable first, before
   * opening the modal to edit profile.
   */
  editProfile() {
    if (
      this.currentUser &&
      this.currentUser.hasProfilePic &&
      !this.currentUser.profilePicture
    ) {
      this.getUserImage(this.currentUser.uuid).subscribe(() => {
        this.openEditProfileDialog();
      });
    } else {
      this.openEditProfileDialog();
    }
  }

  /**
   * Open the modal to edit the profile info for the user.
   */
  openEditProfileDialog() {
    this.dialog
      .open(EditProfileDialogComponent, {
        width: '500px',
        data: this.currentUser,
      })
      .afterClosed()
      .subscribe(userData => {
        if (userData) {
          this.saveProfileUpdates(userData);
        }
      });
  }

  /**
   * Update the profile info of the user, including profile picture.
   *
   * @param  {} userData - data to update
   */
  saveProfileUpdates(userData) {
    this.httpClient
      .put(
        environment.applySubstitutions(environment.api_url) +
          'api/User/' +
          this.currentUser.uuid,
        userData,
      )
      .subscribe(
        () => {
          if (userData.email !== this.currentUser.email) {
            this.router.navigate(['login']);
            return;
          }

          this.currentUser
            .setFirstName(userData.firstname)
            .setLastName(userData.lastname)
            .setRank(userData.rank);

          if (userData.email) {
            this.currentUser.setEmail(userData.email);
          }

          localStorage.setItem(
            AuthService.userKey,
            JSON.stringify(this.currentUser),
          );
          this.currentUserSubject.next(this.currentUser);
        },
        e => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Unable to update profile information.',
              content: e.error.message,
            },
          });
        },
      );

    if (userData.profilePicture) {
      const formData: FormData = new FormData();
      formData.append('file', userData.profilePicture.requestData);
      this.httpClient
        .post(
          environment.applySubstitutions(environment.api_url) +
            'api/User/' +
            this.currentUser.uuid +
            '/image',
          formData,
        )
        .subscribe(() => {
          this.currentUser.setHasProfilePic();
          this.currentUser.profilePicture =
            userData.profilePicture.imageDataUrl;

          localStorage.setItem(
            AuthService.userKey,
            JSON.stringify(this.currentUser),
          );
          this.currentUserSubject.next(this.currentUser);
        });
    }
  }

  resendVerificationEmail(email: string) {
    const url =
      environment.applySubstitutions(environment.api_url) +
      'api/Auth/sendVerificationEmail';
    return this.httpClient.post(url, { email: email });
  }

  sendResetPasswordEmail(email: string) {
    const url =
      environment.applySubstitutions(environment.api_url) + 'api/Auth/recover';
    return this.httpClient.post(url, { email: email });
  }

  approveUserEmail(userUUID: string) {
    const url =
      environment.applySubstitutions(environment.api_url) +
      'api/User/' +
      userUUID +
      '/approve';
    return this.httpClient.post(url, {});
  }

  unapproveUserEmail(userUUID: string) {
    const url =
      environment.applySubstitutions(environment.api_url) +
      'api/User/' +
      userUUID +
      '/approve';
    return this.httpClient.delete(url, {});
  }

  getApprovedDomains() {
    const url =
      environment.applySubstitutions(environment.api_url) +
      'api/Auth/approveddomains';
    return this.httpClient.get(url).pipe(
      map((res: string[]) => {
        this.approvedEmailDomains = res;
      }),
    );
  }

  monitorSession() {
    return interval(30 * 1000).pipe(
      tap(() => {
        if (this.isLoggedIn) {
          const token = localStorage.getItem(AuthService.authTokenKey);
          const decoded: any = jwtDecode(token);

          if (
            !this.extendSessionDialogOpen &&
            this.offline.isOnline() &&
            // current timestamp + 120 seconds exceeds session expiration
            (new Date().valueOf() + (120 * 1000)) / 1000 > decoded.exp
          ) {
            this.dialog
              .open(ExtendSessionComponent)
              .afterClosed()
              .subscribe(() => {
                this.extendSessionDialogOpen = false;
              });
            this.extendSessionDialogOpen = true;
          }
        }
      }),
    );
  }
}
